import React from "react";

export default function AiPolicy() {
  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-3xl mx-auto py-12 px-4">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">AI Disclosure &amp; Safety Policy</h1>
        <p className="text-sm text-gray-500 mb-6">
          <strong>Last Updated:</strong> 4 April 2026<br />
          <strong>Trading Name:</strong> VSENDS<br />
          <strong>Legal Entity:</strong> V CAN RESOURCES LIMITED<br />
          <strong>Company Number:</strong> 13759348<br />
          <strong>Registered Office:</strong> 32 Abbey Drive, Dartford, England, DA2 7WP<br />
          <strong>Support Email:</strong> support@vsends.com<br />
          <strong>Compliance Email:</strong> vg@vcanresources.com
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">1. Introduction</h2>
        <p className="text-gray-700 mb-4">
          V CAN RESOURCES LIMITED ("we", "us", "our") is committed to the responsible and transparent use of artificial intelligence ("AI") in the VSENDS platform. This AI Disclosure &amp; Safety Policy explains how we use AI technologies, the safeguards we have in place, and your rights and responsibilities when using AI-powered features.
        </p>
        <p className="text-gray-700 mb-4">
          As AI technology continues to evolve, we are committed to keeping this policy up to date and being transparent about our AI practices.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">2. AI Features in VSENDS</h2>
        <p className="text-gray-700 mb-4">
          The Service may incorporate AI-powered features to enhance your experience, including but not limited to:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li><strong>Content generation:</strong> AI-assisted email copywriting, subject line suggestions, and content recommendations.</li>
          <li><strong>Send time optimisation:</strong> AI algorithms that predict the best time to send emails to individual recipients.</li>
          <li><strong>Audience segmentation:</strong> AI-powered analysis of recipient behaviour to suggest audience segments.</li>
          <li><strong>Performance prediction:</strong> AI models that forecast campaign performance metrics.</li>
          <li><strong>Spam detection:</strong> AI systems that analyse email content for spam characteristics before sending.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">3. How We Use AI</h2>
        <p className="text-gray-700 mb-4">
          Our AI systems are used to:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>Improve the quality and relevance of email content.</li>
          <li>Optimise delivery timing and frequency for better engagement.</li>
          <li>Detect and prevent spam, abuse, and policy violations.</li>
          <li>Provide analytics and insights to help you make data-driven decisions.</li>
          <li>Automate routine tasks and workflows to save you time.</li>
        </ul>
        <p className="text-gray-700 mb-4">
          AI-generated content is provided as a suggestion only. You remain fully responsible for reviewing, editing, and approving all content before it is sent to recipients.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">4. Data Used by AI Systems</h2>
        <p className="text-gray-700 mb-4">
          Our AI systems may process the following types of data:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>Email content and metadata that you create or upload to the Service.</li>
          <li>Aggregated and anonymised engagement data (open rates, click rates, etc.).</li>
          <li>Historical campaign performance data to improve predictions.</li>
          <li>Technical data about email delivery and infrastructure performance.</li>
        </ul>
        <p className="text-gray-700 mb-4">
          We do not use your personal data or the personal data of your contacts to train third-party AI models. Your data is used solely to provide and improve the Service for your Account. Where we use aggregated data for model improvement, it is fully anonymised and cannot be traced back to you or your contacts.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">5. Accuracy and Limitations</h2>
        <p className="text-gray-700 mb-4">
          AI-generated outputs are not guaranteed to be accurate, complete, or appropriate for your specific use case. AI systems can make errors, produce biased outputs, or generate content that does not meet your expectations.
        </p>
        <p className="text-gray-700 mb-4">
          You should always review AI-generated content before using it. We do not accept liability for any errors, omissions, or issues arising from the use of AI-generated content. You are solely responsible for all content you send through the Service, regardless of whether it was created with AI assistance.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">6. Bias and Fairness</h2>
        <p className="text-gray-700 mb-4">
          We are committed to minimising bias in our AI systems. We take the following steps to address bias:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>Regularly testing our AI models for biased outputs across different demographic groups.</li>
          <li>Using diverse and representative training data where applicable.</li>
          <li>Implementing human review processes for AI-generated content in sensitive contexts.</li>
          <li>Monitoring for and addressing any reports of biased or discriminatory AI outputs.</li>
        </ul>
        <p className="text-gray-700 mb-4">
          Despite our efforts, AI systems may still produce biased outputs. If you encounter any biased or discriminatory content generated by our AI features, please report it to us at vg@vcanresources.com.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">7. Transparency</h2>
        <p className="text-gray-700 mb-4">
          We are committed to being transparent about our use of AI. Where AI is used to generate or modify content within the Service, we will clearly indicate this to you. AI-generated suggestions will be clearly labelled as such.
        </p>
        <p className="text-gray-700 mb-4">
          We will not use AI to make decisions that have significant legal or similarly significant effects on you without your knowledge and consent. You always have the right to know when AI is being used in connection with your Account.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">8. User Responsibilities</h2>
        <p className="text-gray-700 mb-4">
          When using AI-powered features, you agree to:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>Review all AI-generated content before sending it to recipients.</li>
          <li>Ensure that AI-generated content complies with all applicable laws and our Terms of Service.</li>
          <li>Not use AI features to generate misleading, deceptive, or harmful content.</li>
          <li>Not use AI features to impersonate individuals or organisations.</li>
          <li>Not attempt to reverse engineer, manipulate, or exploit our AI systems.</li>
          <li>Report any issues, errors, or concerns with AI-generated content to us.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">9. Third-Party AI Services</h2>
        <p className="text-gray-700 mb-4">
          We may use third-party AI services and APIs to power certain features of the Service. Where we do so, we ensure that:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>The third-party provider has appropriate data protection and security measures in place.</li>
          <li>Data shared with third-party AI providers is limited to what is necessary for the feature to function.</li>
          <li>Third-party providers are bound by data processing agreements that protect your data.</li>
          <li>We do not share personally identifiable information with third-party AI providers without your consent.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">10. Opting Out of AI Features</h2>
        <p className="text-gray-700 mb-4">
          You may choose to disable AI-powered features in your Account settings. Opting out of AI features will not affect your access to the core functionality of the Service.
        </p>
        <p className="text-gray-700 mb-4">
          If you opt out of AI features, we will not process your data through AI systems for the purposes of content generation, recommendations, or predictions. However, AI may still be used for security, spam detection, and infrastructure optimisation purposes, as these are essential to the operation of the Service.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">11. Updates to This Policy</h2>
        <p className="text-gray-700 mb-4">
          We may update this AI Disclosure &amp; Safety Policy as our AI capabilities evolve or as new regulations come into effect. We will notify users of material changes by posting the updated policy on our website with a new "Last Updated" date.
        </p>
        <p className="text-gray-700 mb-4">
          For questions about our use of AI, please contact us at vg@vcanresources.com. We welcome feedback and suggestions on how we can improve our AI practices.
        </p>
      </main>
    </div>
  );
}
